/**
 * PLP product-card image gallery — scalable lazy-hydrate / recycle engine.
 *
 * Only the FIRST image of each card is rendered server-side. The remaining
 * images ship as a compact JSON payload on the track ([data-gallery-more]).
 * This module (loaded ONCE per page, guarded below) hydrates the extra slides
 * only when a card nears the viewport (or on first interaction), and dehydrates
 * them again when it scrolls far away. Decoded-image memory therefore stays
 * bounded by what's near the viewport — not by the number of products — so it
 * holds up on collections of hundreds to thousands of items, including with
 * infinite scroll.
 *
 * Global objects created: exactly two IntersectionObservers, one MutationObserver
 * and a couple of delegated document listeners — regardless of card count.
 */
(function () {
  if (window.__cardGalleryInit) return; // single-load guard (infinite scroll re-injects the tag)
  window.__cardGalleryInit = true;

  var WIDTHS = [360, 533, 720, 940];

  function srcsetFor(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return WIDTHS.map(function (w) {
      return url + sep + 'width=' + w + ' ' + w + 'w';
    }).join(', ');
  }

  function srcFor(url) {
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=533';
  }

  // Load only the slides adjacent to the current position, and unload the ones
  // that are far away — so at most ~3 images per card are ever decoded. Slide 0
  // (the server-rendered featured image) has no galleryUrl and is never touched.
  var LOAD_RADIUS = 1; // load current ±1
  var KEEP_RADIUS = 2; // unload beyond current ±2

  function loadWindow(track) {
    var slides = track.querySelectorAll('.card-gallery__slide');
    if (!slides.length) return;
    var slideW = track.clientWidth || 1;
    var current = Math.round(track.scrollLeft / slideW);

    for (var i = 0; i < slides.length; i++) {
      var img = slides[i].querySelector('img');
      if (!img || !img.dataset.galleryUrl) continue; // skip featured slide 0
      var dist = Math.abs(i - current);
      var url = img.dataset.galleryUrl;

      if (dist <= LOAD_RADIUS && !img.getAttribute('src')) {
        img.srcset = srcsetFor(url);
        img.setAttribute('src', srcFor(url));
      } else if (dist > KEEP_RADIUS && img.getAttribute('src')) {
        // Free the decoded image; it will reload if scrolled back to.
        img.removeAttribute('src');
        img.removeAttribute('srcset');
      }
    }
  }

  function updateGallery(track) {
    var gallery = track.closest('[data-card-gallery]');
    if (!gallery) return;

    var scrollable = track.scrollWidth - track.clientWidth;
    var atStart = track.scrollLeft <= 2;
    var atEnd = track.scrollLeft >= scrollable - 2;

    var thumb = gallery.querySelector('[data-card-gallery-bar-thumb]');
    if (thumb && track.scrollWidth > 0) {
      thumb.style.width = (track.clientWidth / track.scrollWidth) * 100 + '%';
      thumb.style.left = (track.scrollLeft / track.scrollWidth) * 100 + '%';
    }

    var prev = gallery.querySelector('[data-card-gallery-prev]');
    var next = gallery.querySelector('[data-card-gallery-next]');
    // With a single (un-hydrated) slide there is nothing to scroll to.
    var single = scrollable <= 2;
    if (prev) prev.classList.toggle('is-hidden', atStart || single);
    if (next) next.classList.toggle('is-hidden', atEnd || single);
  }

  // --- Hydrate / dehydrate ------------------------------------------------

  function hydrate(gallery) {
    var track = gallery.querySelector('[data-card-gallery-track]');
    if (!track || track.dataset.galleryHydrated === 'true') return;
    track.dataset.galleryHydrated = 'true';

    var payload = track.dataset.galleryMore;
    var items;
    try {
      items = payload ? JSON.parse(payload) : [];
    } catch (e) {
      items = [];
    }
    if (!items.length) return; // nothing beyond the first image

    var href = track.dataset.galleryHref || '#';
    var sizes = track.dataset.gallerySizes || '';
    var frag = document.createDocumentFragment();

    // Build the slide DOM but DON'T set src yet — the image URL is parked in a
    // data attribute. loadWindow() sets the real src only on slides adjacent to
    // the current position, so most images are never decoded.
    items.forEach(function (item) {
      var a = document.createElement('a');
      a.href = href;
      a.className = 'card-gallery__slide';
      a.setAttribute('tabindex', '-1');
      a.setAttribute('aria-hidden', 'true');

      var img = document.createElement('img');
      img.className = 'card-gallery__img motion-reduce';
      img.decoding = 'async';
      if (sizes) img.sizes = sizes;
      img.alt = item.a || '';
      img.dataset.galleryUrl = item.u; // parked; not loaded until adjacent

      a.appendChild(img);
      frag.appendChild(a);
    });

    track.appendChild(frag);
    loadWindow(track); // prime the next slide so the first swipe is ready

    var onScroll = function () {
      if (track._galleryTicking) return;
      track._galleryTicking = true;
      requestAnimationFrame(function () {
        loadWindow(track);
        updateGallery(track);
        track._galleryTicking = false;
      });
    };
    track._galleryScroll = onScroll;
    track.addEventListener('scroll', onScroll, { passive: true });

    updateGallery(track);
  }

  function dehydrate(gallery) {
    var track = gallery.querySelector('[data-card-gallery-track]');
    if (!track || track.dataset.galleryHydrated !== 'true') return;
    track.dataset.galleryHydrated = 'false';

    if (track._galleryScroll) {
      track.removeEventListener('scroll', track._galleryScroll);
      track._galleryScroll = null;
    }

    // Remove every slide except the first (server-rendered) one, freeing the
    // decoded images the browser was holding for this card.
    var slides = track.querySelectorAll('.card-gallery__slide');
    for (var i = slides.length - 1; i >= 1; i--) {
      slides[i].parentNode.removeChild(slides[i]);
    }
    track.scrollLeft = 0;
    updateGallery(track);
  }

  // --- Observers ----------------------------------------------------------
  // Near band (hydrate) is tighter than the far band (dehydrate) to give
  // hysteresis, so a card hovering at the boundary doesn't thrash.

  var hydrateObs =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) hydrate(entry.target);
            });
          },
          { rootMargin: '500px 0px' }
        )
      : null;

  var keepObs =
    typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) dehydrate(entry.target);
            });
          },
          { rootMargin: '1400px 0px' }
        )
      : null;

  function register(gallery) {
    if (gallery.dataset.galleryRegistered === 'true') return;
    gallery.dataset.galleryRegistered = 'true';
    if (hydrateObs && keepObs) {
      hydrateObs.observe(gallery);
      keepObs.observe(gallery);
    } else {
      hydrate(gallery); // no IO support: just hydrate (small/legacy devices)
    }
  }

  function scan(root) {
    (root || document).querySelectorAll('[data-card-gallery]').forEach(register);
  }

  // --- Delegated interaction (hydrate before the observer margin if needed) --

  function hydrateFromEvent(e) {
    var target = e.target;
    if (!target || !target.closest) return;
    var gallery = target.closest('[data-card-gallery]');
    if (gallery) hydrate(gallery);
  }
  document.addEventListener('pointerover', hydrateFromEvent, { passive: true });
  document.addEventListener('touchstart', hydrateFromEvent, { passive: true });

  // Chevrons scroll by one slide (delegated — no per-card binding).
  document.addEventListener('click', function (event) {
    var prev = event.target.closest('[data-card-gallery-prev]');
    var next = event.target.closest('[data-card-gallery-next]');
    if (!prev && !next) return;

    var gallery = (prev || next).closest('[data-card-gallery]');
    var track = gallery && gallery.querySelector('[data-card-gallery-track]');
    if (!track) return;

    event.preventDefault();
    event.stopPropagation();
    hydrate(gallery); // ensure slides exist before scrolling
    track.scrollBy({ left: (next ? 1 : -1) * track.clientWidth, behavior: 'smooth' });
  });

  // --- Boot + observe DOM additions (infinite scroll / AJAX filtering) -----

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (typeof MutationObserver !== 'undefined') {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches('[data-card-gallery]')) register(node);
          if (node.querySelectorAll) scan(node);
        }
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }
})();
